/* eslint-disable no-restricted-globals */
import { RouteComponentProps, navigate } from "@reach/router";
import { FunctionComponent, useContext, useEffect, useState } from "react";
import Button from "../components/button/Button";
import Table, { Column } from "../components/table/Table";
import LayoutContext from "../context/layout-context";
import { initialPaginationData } from "../utils/constants";
import style from "./history.module.scss";
import {
  IssuerConfigCreations,
  RequestResponse,
  SecurityData
} from "../utils/helpers/types";
import ContextMenu, {
  ContextOption
} from "../components/context-menu/ContextMenu";
import { BooleanFilter } from "../utils/helpers/types-helpers";
import Input from "../components/input/Input";

import {
  deleteIssuerConfig,
  getIssuerConfig,
  getIssuerConfigs
} from "../utils/helpers/data/issuer-config";
import { createPortMapping } from "../utils/helpers/data/port-mapping";
import { getSecurity } from "../utils/helpers/data/security";
import AddEditModal from "./addEdit";
import IssuerConfigViewModal from "./issuerConfigViewModal";
import SecurityCreateModal from "./securityCreate";
import PortMappingModal from "./portMappingModal";
import SecurityDataModal from "./securityDataModal";
import SecurityUpdateModal from "./securityUpdate";

const IssuerConfiguration: FunctionComponent<RouteComponentProps> = () => {
  const [showPortMappingModal, setShowPortMappingModal] = useState(false);
  const [portMappingIssuerCode, setPortMappingIssuerCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [reset, setReset] = useState(false);
  const [pagination, setPagination] = useState({ ...initialPaginationData });
  const [issuerConfigOnView, setIssuerConfigOnView] =
    useState<IssuerConfigCreations | null>(null);
  const { notify, confirm } = useContext(LayoutContext);
  const [issuerConfig, setIssuerConfig] = useState<IssuerConfigCreations[]>([]);
  const [issuerConfigOnEdit, setIssuerConfigOnEdit] =
    useState<IssuerConfigCreations | null>(null);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [issuerCode, setIssuerCode] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [securityData, setSecurityData] = useState(null);
  const showResetFilter = issuerCode || name;

  const [showSecurityCreateModal, setShowSecurityCreateModal] = useState(false);
  const [showSecurityUpdateModal, setShowSecurityUpdateModal] = useState(false);
  const [selectedIssuerCode, setSelectedIssuerCode] = useState("");

  const [formData, setFormData] = useState<SecurityData | undefined>(
    securityData ?? undefined
  );

  useEffect(() => {
    if (securityData !== null) {
      setFormData(securityData);
    } else {
      setFormData(undefined);
    }
  }, [securityData]);

  const handleCreateOrUpdateSecurity = async (issuerCode: string) => {
    setSelectedIssuerCode(issuerCode);
    setLoading(true);
    const response = await fetchSecurityData(issuerCode, "createOrUpdate");
    if (!response.error && response.data) {
      setSecurityData(response.data);
      setShowSecurityUpdateModal(true);
    } else {
      setSecurityData(null);
      setShowSecurityCreateModal(true);
    }
    setLoading(false);
  };

  const resetFilters = () => {
    setIssuerCode("");
    setName("");
    setPagination({ ...pagination, pageNumber: 1 });
    setReset(true);
  };

  const displayAddEditModal = async (
    issuerConfig: IssuerConfigCreations | null
  ) => {
    if (issuerConfig) {
      const { id } = issuerConfig;
      const result = await getIssuerConfig(id);
      if (!result.error) {
        setIssuerConfigOnEdit(result.data);
        setShowAddEditModal(true);
      } else {
        notify("error", `Unable to fetch issuer config: ${result.message}`);
      }
    } else {
      setIssuerConfigOnEdit(null);
      setShowAddEditModal(true);
    }
  };
  const dismissAddEditModal = () => {
    setIssuerConfigOnEdit(null);
    setShowAddEditModal(false);
  };

  const fetchIssuerConfig = async (
    pageNumber = pagination.pageNumber,
    pageSize = pagination.pageSize
  ) => {
    setLoading(true);
    const { data, error, message } = await getIssuerConfigs(
      pageNumber,
      pageSize,
      issuerCode,
      name
    );
    setLoading(false);

    if (error || !data) {
      notify("error", `Unable to issuer config: ${message}`);
      return;
    }

    setIssuerConfig(data.records);
    setPagination({
      ...pagination,
      pageNumber,
      total: data.total
    });

    setReset(false);
  };

  useEffect(() => {
    fetchIssuerConfig();
  }, [pagination.pageSize]);

  const handleViewReport = async () => {
    setLoading(true);
    try {
      setPagination({ ...pagination, pageNumber: 1 });
      await fetchIssuerConfig(pagination.pageNumber);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      notify("error", "An error occurred while fetching map acquirer.", error);
    }
  };

  useEffect(() => {
    if (!reset) {
      fetchIssuerConfig();
    } else {
      setPagination({ ...initialPaginationData });
      setReset(false);
    }
  }, [reset]);

  const handleOperation = async (operation: any, id: any) => {
    setLoading(true);
    let result;

    switch (operation) {
      case "delete":
        result = await deleteIssuerConfig(id);
        break;

      case "view":
        result = await getIssuerConfig(id);
        if (!result.error) {
          setIssuerConfigOnView(result.data);
        }
        break;
      default:
        setLoading(false);
        return;
    }

    setLoading(false);

    if (result.error) {
      notify(
        "error",
        `Unable to ${operation} issuer config: ${result.message}`
      );
      return;
    }

    notify("success", `issuer config ${operation}d successfully`);
  };

  const handleDelete = async (issuerConfig: IssuerConfigCreations) => {
    confirm({
      title: "Are you sure you want to delete this issuer config?",
      body: "This action cannot be undone.",
      onOk: async () => {
        setLoading(true);
        const { error, message } = await deleteIssuerConfig(issuerConfig.id);
        setLoading(false);

        if (error) {
          notify("error", `Unable to delete issuer config: ${message}`);
          return;
        }

        notify("success", "Issuer config deleted successfully");
        await fetchIssuerConfig();
      }
    });
  };

  const fetchSecurityData = async (
    issuerCode: string,
    operation: string
  ): Promise<RequestResponse> => {
    try {
      const response = await getSecurity(issuerCode);
      if (!response.error) {
        if (
          operation !== "createOrUpdate" &&
          response.message &&
          !response.message.includes(
            "Could not find security for issuer config"
          )
        ) {
          notify(
            "success",
            response.message || "Issuer security fetched successfully"
          );
        }
      } else if (
        operation !== "createOrUpdate" &&
        response.message &&
        !response.message.includes(
          "Security fetched for issuer config with code"
        )
      ) {
        notify("error", response.message || "Unknown error occurred");
      }
      return response;
    } catch (err: any) {
      console.error("Unable to fetch security data: ", err);
      if (operation !== "createOrUpdate") {
        notify(
          "error",
          err.response?.responseMessage ||
            err.message ||
            "Unknown error occurred"
        );
      }
      return {
        error: true,
        message:
          err.response?.responseMessage ||
          err.message ||
          "Unknown error occurred",
        data: null
      };
    }
  };

  const displayPortMappingModal = (issuerConfig: IssuerConfigCreations) => {
    const { issuerCode } = issuerConfig;
    setShowPortMappingModal(true);
    setPortMappingIssuerCode(issuerCode);
  };

  const handleSubmitPortMapping = async (formData: {
    issuerCode: string;
    port: number;
    ipAddress: string;
  }) => {
    const { error, message } = await createPortMapping(formData);
    if (error) {
      notify("error", `Unable to create port mapping: ${message}`);
    } else {
      notify("success", "Port mapping created successfully");
      navigate("/port-mapping");
    }
  };

  const handleSecurityClick = async (issuerCode: any) => {
    setLoading(true);
    const response = await fetchSecurityData(issuerCode, "view");
    if (!response.error) {
      setSecurityData(response.data);
      setShowSecurityModal(true);
    }
    setLoading(false);
  };

  const columns: Column<IssuerConfigCreations>[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name"
    },
    {
      title: "Issuer Code",
      dataIndex: "issuerCode",
      key: "issuerCode"
    },

    {
      title: "Wallet Id",
      dataIndex: "walletId",
      key: "walletId"
    },
    {
      title: "API Version",
      dataIndex: "apiVersion",
      key: "apiVersion"
    },

    {
      title: "",
      render: (_: unknown, issuerConfig: IssuerConfigCreations) => {
        const options: ContextOption[] = [
          {
            label: "View Details",
            onClick: () => {
              handleOperation("view", issuerConfig.id);
            }
          },
          {
            label: "Edit",
            onClick: () => {
              displayAddEditModal(issuerConfig);
            }
          },
          {
            label: "Create Port Mapping",
            onClick: () => displayPortMappingModal(issuerConfig)
          },
          {
            label: "View Security",
            onClick: () => handleSecurityClick(issuerConfig.issuerCode)
          },
          {
            label: "Create/Update security",
            onClick: () => handleCreateOrUpdateSecurity(issuerConfig.issuerCode)
          },
          {
            label: "Delete",
            onClick: () => handleDelete(issuerConfig)
          }
        ].filter(BooleanFilter) as ContextOption[];
        return <ContextMenu options={options} />;
      }
    }
  ];

  return (
    <main>
      <h1 className="title vertical-margin spaced thin">
        Issuer Configuration
      </h1>

      <div className="vertical-margin spaced">
        <div className={style.cont}>
          <div className={style.container}>
            <div className={style.cont1}>
              <div className="input-group half-width">
                <Input
                  value={issuerCode}
                  onChange={(value: string) => setIssuerCode(value)}
                  placeholder="Enter issuer code or name"
                />
              </div>
            </div>

            <div className={style.inputView}>
              <Button
                type="accent"
                size="large"
                className="strong no-pdf-print"
                onClick={handleViewReport}
              >
                Filter record
              </Button>

              <span className={style.button} />
            </div>
          </div>
        </div>
        {showResetFilter && (
          <Button type="accent" size="small" onClick={resetFilters}>
            RESET FILTERS
          </Button>
        )}
      </div>

      <br />
      <br />

      <div className="vertical-margin flex end">
        <Button onClick={() => displayAddEditModal(null)}>
          Create Issuer Config
        </Button>
      </div>

      <Table
        dataSource={issuerConfig || []}
        columns={columns}
        pagination={pagination}
        onPageChange={fetchIssuerConfig}
        onPageSizeChange={pageSize =>
          setPagination({ ...pagination, pageSize, pageNumber: 1 })
        }
        loading={loading}
        className="smaller"
      />

      <IssuerConfigViewModal
        issuerConfig={issuerConfigOnView}
        cancel={() => setIssuerConfigOnView(null)}
        visible={Boolean(issuerConfigOnView)}
      />

      <AddEditModal
        visible={showAddEditModal}
        issuerConfigOnEdit={issuerConfigOnEdit}
        cancel={dismissAddEditModal}
        refresh={(stayOnPage: any) =>
          fetchIssuerConfig(stayOnPage ? pagination.pageNumber : 1)
        }
      />

      {showPortMappingModal && (
        <PortMappingModal
          issuerCode={portMappingIssuerCode}
          onCancel={() => setShowPortMappingModal(false)}
          onSubmit={handleSubmitPortMapping}
        />
      )}

      <SecurityDataModal
        securityData={securityData}
        onCancel={() => setShowSecurityModal(false)}
        visible={showSecurityModal}
      />

      <SecurityCreateModal
        visible={showSecurityCreateModal}
        onCancel={() => setShowSecurityCreateModal(false)}
        issuerCode={selectedIssuerCode}
        onSuccess={() => {
          setShowSecurityCreateModal(false);

          fetchIssuerConfig();
        }}
      />

      <SecurityUpdateModal
        visible={showSecurityUpdateModal}
        onCancel={() => setShowSecurityUpdateModal(false)}
        issuerCode={selectedIssuerCode}
        onSuccess={() => {
          setShowSecurityUpdateModal(false);

          fetchIssuerConfig();
        }}
        securityData={formData}
      />
    </main>
  );
};

export default IssuerConfiguration;
